/**
 * TakenList — "Opvolgen"-werkvoorraad op /telefoon.
 *
 * Consumeert het generieke taken-primitief (/api/tasks). Telefonie is de eerste
 * producer: een gemiste oproep of voicemail wordt automatisch een opvolgtaak.
 * Toewijzen (teamwachtrij), due, snooze, afspelen + transcript (voicemail),
 * terugbellen, afhandelen, en toetsenbord-triage (j/k, c, a, e, x, s, o).
 * De kaart toont wie er belt (klantmatch van de server: bekende klant met
 * laatste order, offerte-aanvrager, of nog onbekend) en laat het gesprek
 * nalezen; alleen Terugbellen en Afgehandeld staan als knop, de rest zit
 * achter Meer.
 *
 * De AudioPlayer/transcript spiegelen VoicemailList.tsx (dezelfde audioproxy).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { usePhoneActions } from '../../hooks/usePhoneActions';
import { useAuthStore } from '../../stores';
import type { Brand } from '../../lib/telephony';
import { PhoneNumber } from './PhoneNumber';
import { TakenKlantPanel } from './TakenKlantPanel';
import { TakenKlantDrawer } from './TakenKlantDrawer';
import { labelFor } from '../../lib/telephony/belredenen';

interface TaskCall {
  id: string;
  fromNumber: string | null;
  recordingAvailable: boolean;
  durationSeconds: number | null;
  transcript: string | null;
  /** Gespreksfragmenten (voice-agent/opgenomen gesprek); null bij voicemail. */
  segments: Array<{ role: string; text: string }> | null;
  summary: string | null;
  processingStatus: string | null;
  startedAt: string | null;
}

interface TaskKlant {
  type: 'account' | 'lead';
  id: string;
  name: string | null;
  city: string | null;
  order: { id: string; name: string; status: string | null } | null;
}

interface TaskRow {
  id: string;
  source: string;
  sourceRef: string | null;
  customerId: string | null;
  customerName: string | null;
  /**
   * Het nummer waarop je terugbelt, door de server samengesteld uit
   * ops_tasks.phone met de bron-call als terugval. De kaart hoeft dus niet te
   * weten of een taak uit voicemail, gemist of de voice-agent komt.
   */
  callbackNumber: string | null;
  brand: string | null;
  title: string;
  body: string | null;
  category: string | null;
  status: string;
  assignee: string | null;
  dueAt: string | null;
  snoozedUntil: string | null;
  createdAt: string;
  call: TaskCall | null;
  /** Klant/lead achter het nummer, door de server gematcht (null = onbekend). */
  klant: TaskKlant | null;
}

type Filter = 'open' | 'resolved';

/**
 * Eén kaart per beller. Iemand die twee keer belt (voicemail én agent, of
 * twee keer over dezelfde offerte) levert twee taken op, maar je belt hem één
 * keer terug. De groep neemt de plek in van zijn oudste taak (FIFO blijft) en
 * toont het nieuwste gesprek bovenaan; afhandelen sluit alle taken tegelijk.
 * Alleen op de open-lijst: afgehandeld is een logboek, daar wil je elke taak
 * apart terugzien.
 */
interface TaskGroup {
  key: string;
  primary: TaskRow;
  rest: TaskRow[];
  ids: string[];
}

function groupByNumber(rows: TaskRow[], enabled: boolean): TaskGroup[] {
  if (!enabled) return rows.map((r) => ({ key: r.id, primary: r, rest: [], ids: [r.id] }));
  const byKey = new Map<string, TaskRow[]>();
  for (const r of rows) {
    const key = r.callbackNumber ?? r.id;
    const list = byKey.get(key);
    if (list) list.push(r);
    else byKey.set(key, [r]);
  }
  return Array.from(byKey.entries()).map(([key, list]) => {
    const sorted = [...list].sort(
      (a, b) => new Date(b.call?.startedAt ?? b.createdAt).getTime() - new Date(a.call?.startedAt ?? a.createdAt).getTime(),
    );
    return { key, primary: sorted[0]!, rest: sorted.slice(1), ids: list.map((r) => r.id) };
  });
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  return headers;
}

/**
 * Voicemail = bron voicemail, of een gemiste oproep waar toch een opname aan
 * hangt. Niet "er is een opname": voice-agent-gesprekken zijn óók opgenomen,
 * en die als voicemail labelen zette een Afspelen-knop op een gesprek dat je
 * beter naleest. De speler krijgt altijd call.id, nooit de (opake) source_ref.
 */
function heeftVoicemail(t: TaskRow): boolean {
  if (t.source === 'voicemail') return true;
  return t.source === 'missed_call' && Boolean(t.call?.recordingAvailable);
}

/** De voice-agent zet 'Nummer: +31…' onderaan de body; dat staat al in de kop. */
function stripNummerRegel(body: string): string {
  return body.replace(/\n?Nummer: \+?[\d ]+\s*$/i, '').trim();
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function AudioPlayer({ callId }: { callId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/telephony/voicemail?callId=${encodeURIComponent(callId)}`, {
        headers: await authHeaders(),
      });
      if (!r.ok) throw new Error(`audio failed: ${r.status}`);
      const blob = await r.blob();
      setSrc(URL.createObjectURL(blob));
    } catch {
      setSrc(null);
    } finally {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => () => {
    if (src) URL.revokeObjectURL(src);
  }, [src]);

  if (src) {
    return <audio controls autoPlay src={src} className="h-8 w-full max-w-xs" />;
  }
  return (
    <button
      type="button"
      onClick={load}
      disabled={loading}
      className="text-xs text-indigo-600 hover:text-indigo-800 underline"
    >
      {loading ? 'Laden…' : 'Afspelen'}
    </button>
  );
}

/**
 * Het gesprek nalezen. Voice-agent en opgenomen gesprekken hebben fragmenten
 * met een rol (chatweergave, zelfde look als de Gesprekken-tab); een voicemail
 * heeft alleen platte transcriptie. Dicht bij openen: de samenvatting staat al
 * op de kaart, dit is voor als je wilt weten wat er letterlijk gezegd is.
 */
function Gesprek({ call }: { call: TaskCall }) {
  const [open, setOpen] = useState(false);
  const segments = call.segments?.filter((s) => s.text?.trim()) ?? [];
  const hasSegments = segments.length > 0;
  if (!hasSegments && !call.transcript) return null;
  return (
    <div className="mt-2 bg-slate-50 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
      >
        <span>Gesprek nalezen{hasSegments ? ` (${segments.length} fragmenten)` : ''}</span>
        <span className="text-slate-400">{open ? 'Verbergen' : 'Tonen'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5 max-h-[360px] overflow-y-auto">
          {hasSegments ? (
            segments.map((seg, i) => {
              const isAgent = seg.role === 'medewerker';
              return (
                <div key={i} className={clsx('flex', isAgent ? 'justify-end' : 'justify-start')}>
                  <div
                    className={clsx(
                      'max-w-[85%] rounded-lg px-3 py-1.5 text-sm',
                      isAgent ? 'bg-indigo-100 text-indigo-900' : 'bg-white border border-slate-200 text-slate-800',
                    )}
                  >
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                      {isAgent ? 'Telefoon-assistent' : 'Beller'}
                    </p>
                    {seg.text}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{call.transcript}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Secundaire acties achter één knop. Kantoor gebruikt vrijwel alleen
 * Terugbellen en Afgehandeld; de rest blijft bereikbaar (ook via sneltoets)
 * maar concurreert niet meer om aandacht op elke kaart.
 */
function MeerMenu({ items }: { items: Array<{ label: string; onClick: () => void }> }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Meer acties"
        className="px-2 py-1.5 text-slate-500 hover:bg-slate-100 text-xs rounded"
      >
        Meer
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[140px]">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `kind` scheidt de twee werkvoorraden die vroeger door elkaar liepen:
 * 'telefoon' = terugbellen (voicemail/gemist) op /telefoon, 'systeem' = signalen
 * uit convert, doorverwijs, financieel enz. op /signalen. De serverkant beslist
 * wat waarin valt (TELEFONIE_SOURCES in api/_lib/tasks.ts) — hier staat bewust
 * geen tweede bronnenlijst.
 */
export function TakenList({
  kind = 'telefoon',
  onCountChange,
}: {
  kind?: 'telefoon' | 'systeem';
  onCountChange?: (openCount: number) => void;
}) {
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [filter, setFilter] = useState<Filter>('open');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState(0);
  const [contextTask, setContextTask] = useState<TaskRow | null>(null);
  const { openDialPad } = usePhoneActions();
  const myEmail = useAuthStore((s) => s.user?.email)?.toLowerCase() ?? null;
  const listRef = useRef<HTMLUListElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const fetchRows = useCallback(async (f: Filter) => {
    setIsLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/tasks?status=${f}&kind=${kind}`, { headers: await authHeaders() });
      if (!r.ok) throw new Error(`tasks failed: ${r.status}`);
      const json = await r.json();
      setRows(json.tasks ?? []);
      setSelected(0);
      if (f === 'open') onCountChange?.(json.total ?? (json.tasks ?? []).length);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [kind, onCountChange]);

  useEffect(() => {
    void fetchRows(filter);
  }, [fetchRows, filter]);

  // Eén of meer taken (een gegroepeerde beller), daarna één refetch.
  const patch = useCallback(
    async (taskIds: string | string[], body: Record<string, unknown>) => {
      const ids = Array.isArray(taskIds) ? taskIds : [taskIds];
      try {
        const headers = await authHeaders();
        const results = await Promise.all(
          ids.map((taskId) =>
            fetch('/api/tasks', { method: 'PATCH', headers, body: JSON.stringify({ taskId, ...body }) }),
          ),
        );
        const failed = results.find((r) => !r.ok);
        if (failed) throw new Error(`patch failed: ${failed.status}`);
        await fetchRows(filter);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [fetchRows, filter],
  );

  const snooze = useCallback(
    (taskId: string | string[], hours: number) => {
      const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
      return patch(taskId, { snoozedUntil: until });
    },
    [patch],
  );

  // Zet het nummer klaar in de softphone (dialpad opent voorgevuld) i.p.v. direct
  // te bellen — kantoor klikt zelf op Bellen zodra ze er klaar voor zijn.
  const callBack = useCallback(
    (t: TaskRow) => {
      const num = t.callbackNumber;
      if (!num) return;
      openDialPad({
        number: num,
        brand: (t.brand as Brand) ?? undefined,
        customerId: t.customerId ?? undefined,
        customerName: t.customerName ?? undefined,
        sourceContext: 'opvolgen_terugbellen',
        // Terugbel-lus (22-10): de taak reist mee naar calls.context_*, zodat de
        // voice-agent weet waarover wij belden als de klant terugbelt.
        contextTaskId: t.id,
        contextReden: t.title,
      });
    },
    [openDialPad],
  );

  const groups = useMemo(() => groupByNumber(rows, filter === 'open'), [rows, filter]);

  const openContext = useCallback((t: TaskRow) => {
    if (t.klant || t.customerId || t.callbackNumber) setContextTask(t);
  }, []);

  // Toetsenbord-triage op de open-lijst (zoals de inbox). Werkt op de
  // geselecteerde rij; in split-weergave blijft de lijst actief naast het
  // klant/WO-paneel, en volgt het paneel de selectie mee (j/k). Negeer toetsen
  // wanneer de focus in een invoerveld/audio zit of wanneer je in het
  // rechterpaneel zelf bezig bent (dat vangt zijn eigen toetsen).
  useEffect(() => {
    if (filter !== 'open') return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'AUDIO' || tag === 'SELECT') return;
      if (rightPanelRef.current?.contains(e.target as Node)) return;
      const g = groups[selected];
      const t = g?.primary;
      switch (e.key) {
        case 'j': {
          const next = Math.min(selected + 1, groups.length - 1);
          setSelected(next);
          if (contextTask && groups[next]) openContext(groups[next].primary);
          break;
        }
        case 'k': {
          const prev = Math.max(selected - 1, 0);
          setSelected(prev);
          if (contextTask && groups[prev]) openContext(groups[prev].primary);
          break;
        }
        case 'c':
          if (t) callBack(t);
          break;
        case 'a':
          if (g && t) void patch(g.ids, { assignee: t.assignee ? null : myEmail });
          break;
        case 'e':
          if (g) void patch(g.ids, { status: 'done' });
          break;
        case 'x':
          if (g) void patch(g.ids, { status: 'cancelled' });
          break;
        case 's':
          if (g) void snooze(g.ids, 3);
          break;
        case 'o':
        case 'Enter':
          if (t) openContext(t);
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filter, contextTask, groups, selected, callBack, patch, snooze, myEmail, openContext]);

  const emptyText = useMemo(() => {
    if (filter !== 'open') return 'Nog niks afgehandeld.';
    return kind === 'systeem'
      ? 'Geen open signalen. Alles loopt.'
      : 'Niks om op te volgen. Mooi zo.';
  }, [filter, kind]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {(
          [
            ['open', 'Open'],
            ['resolved', 'Afgehandeld'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setFilter(id)}
            className={clsx(
              'px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
              filter === id ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            {label}
          </button>
        ))}
        {filter === 'open' && rows.length > 0 && (
          <span className="ml-auto text-[11px] text-slate-400">
            Sneltoetsen: j/k · c bellen · e afgehandeld · o klant/WO
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{error}</div>
      )}

      {/* Geen 50/50: links staat prose (voicemail-samenvattingen) en die wordt
          voorbij ~770px juist slechter te scannen, rechts staat dichte data in
          geneste kolommen die elke pixel gebruikt. De lijst loopt dus tot
          max-w-3xl en het paneel pakt de rest; op smallere schermen delen ze
          gelijk op tot het paneel zijn minimum raakt en de lijst wijkt. */}
      <div className="flex gap-4 items-start">
        {/* Breedte in px, niet via max-w-3xl: de app zet de root op 14px, dus
            elke rem-klasse rendert 12,5% smaller dan de Tailwind-docs zeggen
            (max-w-3xl = 48rem = 672px hier, niet 768). Basis expliciet omdat
            flex de vrije ruimte anders naar inhoudsbreedte verdeelt. */}
        <div className="min-w-0 flex-1 basis-[672px] max-w-[672px]">
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
          {emptyText}
        </div>
      ) : (
        <ul className="space-y-3" ref={listRef}>
          {groups.map((g, idx) => {
            const t = g.primary;
            const isVoicemail = heeftVoicemail(t);
            const num = t.callbackNumber;
            const mine = t.assignee && myEmail && t.assignee.toLowerCase() === myEmail;
            // Naam uit de klantmatch wint van wat de beller zelf opgaf.
            const naam = t.klant?.name || t.customerName || null;
            // De titel is bij telefoontaken altijd 'Voice-agent: …'/'Voicemail van …';
            // als korte bronaanduiding leest dat beter dan als kop.
            const bron =
              t.source === 'voice_agent' ? 'via telefoon-assistent'
              : t.source === 'call' ? 'afgesproken in een gesprek'
              : t.source === 'missed_call' ? 'gemiste oproep'
              : t.source === 'bellijst' ? (t.category === 'aflever_plannen' ? 'bellijst: aflevering plannen' : 'bellijst: ophalen plannen')
              : null;
            return (
              <li
                key={g.key}
                onClick={() => {
                  setSelected(idx);
                  openContext(t);
                }}
                className={clsx(
                  'bg-white border rounded-xl p-4 transition-colors cursor-pointer',
                  contextTask?.id === t.id
                    ? 'border-indigo-400 ring-1 ring-indigo-200'
                    : filter === 'open' && idx === selected
                      ? 'border-indigo-300 ring-1 ring-indigo-100'
                      : 'border-slate-200 hover:border-slate-300',
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-900">
                        {naam
                          ? naam
                          : num
                            ? (
                                <PhoneNumber
                                  number={num}
                                  brand={(t.brand as Brand) ?? undefined}
                                  customerId={t.customerId ?? undefined}
                                  customerName={t.customerName ?? undefined}
                                  sourceContext="voicemail"
                                />
                              )
                            : 'Onbekend nummer'}
                      </span>
                      {/* Wie belt er: bekende klant, openstaande offerte of iemand
                          die we nog niet kennen. Dat is het eerste wat je wilt
                          weten vóór je terugbelt. */}
                      {t.klant?.type === 'account' ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">Bekende klant</span>
                      ) : t.klant?.type === 'lead' ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 font-medium">Offerte-aanvrager</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Nog onbekend</span>
                      )}
                      {g.rest.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">
                          {g.rest.length + 1}x gebeld
                        </span>
                      )}
                      {t.brand && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          {t.brand}
                        </span>
                      )}
                      {isVoicemail && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Voicemail</span>
                      )}
                      {t.category && t.category !== 'overig' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{labelFor(t.category)}</span>
                      )}
                      {mine && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">Van mij</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {formatDateTime(t.call?.startedAt ?? t.createdAt)}
                      {bron ? ` · ${bron}` : ''}
                      {naam && num ? ` · ${num}` : ''}
                      {t.klant?.city ? ` · ${t.klant.city}` : ''}
                      {t.klant?.order ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openContext(t);
                            }}
                            className="text-indigo-600 hover:text-indigo-800 underline"
                          >
                            {t.klant.order.name}
                          </button>
                          {t.klant.order.status ? ` (${t.klant.order.status})` : ''}
                        </>
                      ) : null}
                      {t.assignee && !mine ? ` · toegewezen: ${t.assignee}` : ''}
                    </div>
                  </div>
                  {isVoicemail && t.call && (
                    <span className="contents" onClick={(e) => e.stopPropagation()}>
                      <AudioPlayer callId={t.call.id} />
                    </span>
                  )}
                </div>

                {t.body ? (
                  <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{stripNummerRegel(t.body)}</p>
                ) : t.call?.processingStatus && t.call.processingStatus !== 'done' && isVoicemail ? (
                  <p className="mt-2 text-xs text-slate-400 italic">Transcriptie en samenvatting volgen automatisch…</p>
                ) : null}

                {t.call && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Gesprek call={t.call} />
                  </div>
                )}

                {g.rest.map((r) => (
                  <div key={r.id} className="mt-3 pl-3 border-l-2 border-slate-200" onClick={(e) => e.stopPropagation()}>
                    <div className="text-xs text-slate-500">
                      Eerder: {formatDateTime(r.call?.startedAt ?? r.createdAt)}
                      {r.source === 'voice_agent' ? ' · via telefoon-assistent' : heeftVoicemail(r) ? ' · voicemail' : ''}
                    </div>
                    {r.body && <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{stripNummerRegel(r.body)}</p>}
                    {heeftVoicemail(r) && r.call && <AudioPlayer callId={r.call.id} />}
                    {r.call && <Gesprek call={r.call} />}
                  </div>
                ))}

                <div className="mt-3 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {num && (
                    <button
                      type="button"
                      onClick={() => callBack(t)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded"
                    >
                      Terugbellen
                    </button>
                  )}
                  {filter === 'open' ? (
                    <>
                      <button
                        type="button"
                        onClick={() => void patch(g.ids, { status: 'done' })}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded"
                      >
                        Afgehandeld
                      </button>
                      <MeerMenu
                        items={[
                          { label: 'Niet nodig', onClick: () => void patch(g.ids, { status: 'cancelled' }) },
                          { label: 'Snooze 3 uur', onClick: () => void snooze(g.ids, 3) },
                          { label: mine ? 'Vrijgeven' : 'Aan mij', onClick: () => void patch(g.ids, { assignee: mine ? null : myEmail }) },
                        ]}
                      />
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void patch(t.id, { status: 'open' })}
                      className="px-3 py-1.5 text-slate-500 hover:bg-slate-100 text-xs rounded"
                    >
                      Heropenen
                    </button>
                  )}
                  {(t.customerId || num) && (
                    <button
                      type="button"
                      onClick={() => openContext(t)}
                      className="ml-auto text-xs text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Klant / WO
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
        </div>

        {/* Breed genoeg voor twee kolommen: paneel ernaast, lijst blijft staan.
            Te smal: dan is er geen eerlijke splitsing te maken en pakt de
            slide-over het over (dezelfde die de Gesprekken-tab gebruikt), zodat
            de lijst ook daar niet van breedte verspringt. */}
        {contextTask && (
          <div
            ref={rightPanelRef}
            className="sticky top-4 self-start hidden lg:flex flex-1 basis-[600px] min-w-[480px] h-[calc(100vh-8rem)]"
          >
            <TakenKlantPanel
              key={contextTask.id}
              customerId={contextTask.klant?.id ?? contextTask.customerId}
              customerType={contextTask.klant?.type ?? null}
              phone={contextTask.callbackNumber}
              fallbackName={contextTask.klant?.name ?? contextTask.customerName}
              onClose={() => setContextTask(null)}
            />
          </div>
        )}
      </div>

      {contextTask && (
        <div className="lg:hidden">
          <TakenKlantDrawer
            key={contextTask.id}
            customerId={contextTask.klant?.id ?? contextTask.customerId}
            customerType={contextTask.klant?.type ?? null}
            phone={contextTask.callbackNumber}
            fallbackName={contextTask.klant?.name ?? contextTask.customerName}
            onClose={() => setContextTask(null)}
          />
        </div>
      )}
    </div>
  );
}
